import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Bell, Moon, Shield, Settings as SettingsIcon } from "lucide-react"; // Renamed Key to SettingsIcon
import { apiRequest } from "@/lib/queryClient";
import { useTheme } from "@/hooks/use-theme";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const preferencesSchema = z.object({
  emailNotifications: z.boolean(),
  marketingEmails: z.boolean(),
  weeklyDigest: z.boolean(),
  darkMode: z.boolean(),
  autoSave: z.boolean(),
  showWordCount: z.boolean(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const llmSettingsSchema = z.object({
  blueprintService: z.string().min(1, "LLM Service is required"),
  blueprintModel: z.string().min(1, "LLM Model is required"),
  chaptersOutlineService: z.string().min(1, "LLM Service is required"),
  chaptersOutlineModel: z.string().min(1, "LLM Model is required"),
  chaptersTextService: z.string().min(1, "LLM Service is required"),
  chaptersTextModel: z.string().min(1, "LLM Model is required"),
});

type PreferencesFormData = z.infer<typeof preferencesSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;
type LLMSettingsFormData = z.infer<typeof llmSettingsSchema>;

interface UserData {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  planCode?: string;
  role?: string;
  llmSettings?: {
    blueprintService: string;
    blueprintModel: string;
    chaptersOutlineService: string;
    chaptersOutlineModel: string;
    chaptersTextService: string;
    chaptersTextModel: string;
  };
}

// Mock data for available LLM services and models
const availableLLMServices = [
  { id: "openai", name: "OpenAI" },
  { id: "google", name: "Google Gemini" },
  { id: "deepseek", name: "DeepSeek" },
];

const availableLLMModels = {
  openai: [
    { id: "gpt-4o", name: "GPT-4o", multiplier: 1.5 },
    { id: "gpt-4-turbo", name: "GPT-4 Turbo", multiplier: 1.2 },
    { id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo", multiplier: 1.0 },
  ],
  google: [
    { id: "gemini-pro", name: "Gemini Pro", multiplier: 1.1 },
    { id: "gemini-flash", name: "Gemini Flash", multiplier: 0.9 },
  ],
  deepseek: [
    { id: "deepseek-chat", name: "DeepSeek Chat", multiplier: 0.8 },
    { id: "deepseek-r1", name: "DeepSeek-R1", multiplier: 1.0 },
  ],
};

export default function SettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isDark, setDark } = useTheme();

  const { data: user } = useQuery<UserData>({
    queryKey: ["/api/auth/me"],
  });

  const preferencesForm = useForm<PreferencesFormData>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      emailNotifications: true,
      marketingEmails: false,
      weeklyDigest: true,
      darkMode: isDark,
      autoSave: true,
      showWordCount: true,
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const llmSettingsForm = useForm<LLMSettingsFormData>({
    resolver: zodResolver(llmSettingsSchema),
    defaultValues: {
      blueprintService: user?.llmSettings?.blueprintService || "openai",
      blueprintModel: user?.llmSettings?.blueprintModel || "gpt-4o",
      chaptersOutlineService: user?.llmSettings?.chaptersOutlineService || "openai",
      chaptersOutlineModel: user?.llmSettings?.chaptersOutlineModel || "gpt-4o",
      chaptersTextService: user?.llmSettings?.chaptersTextService || "openai",
      chaptersTextModel: user?.llmSettings?.chaptersTextModel || "gpt-4o",
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (data: PreferencesFormData) =>
      apiRequest("/api/user/preferences", "PATCH", data),
    onSuccess: () => {
      toast({
        title: "Preferences updated",
        description: "Your preferences have been successfully updated.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: (data: PasswordFormData) =>
      apiRequest("/api/user/password", "PATCH", data),
    onSuccess: () => {
      toast({
        title: "Password updated",
        description: "Your password has been successfully changed.",
      });
      passwordForm.reset();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update password. Please check your current password.",
        variant: "destructive",
      });
    },
  });

  const updateLLMSettingsMutation = useMutation({
    mutationFn: (data: LLMSettingsFormData) => {
      return apiRequest("/api/user/llm-settings", "PUT", data);
    },
    onSuccess: () => {
      toast({
        title: "LLM Settings updated",
        description: "Your LLM preferences have been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update LLM settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmitPreferences = (data: PreferencesFormData) => {
    updatePreferencesMutation.mutate(data);
  };

  const onSubmitPassword = (data: PasswordFormData) => {
    updatePasswordMutation.mutate(data);
  };

  const onSubmitLLMSettings = (data: LLMSettingsFormData) => {
    updateLLMSettingsMutation.mutate(data);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your application preferences and configurations.
          </p>
        </div>

        <Tabs defaultValue="preferences" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="llm-settings">LLM Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="preferences" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notifications
                </CardTitle>
                <CardDescription>
                  Configure how you receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...preferencesForm}>
                  <form onSubmit={preferencesForm.handleSubmit(onSubmitPreferences)} className="space-y-6">
                    <FormField
                      control={preferencesForm.control}
                      name="emailNotifications"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Email Notifications</FormLabel>
                            <FormDescription>
                              Receive important updates via email
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={preferencesForm.control}
                      name="marketingEmails"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Marketing Emails</FormLabel>
                            <FormDescription>
                              Receive updates about new features and tips
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={preferencesForm.control}
                      name="weeklyDigest"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Weekly Digest</FormLabel>
                            <FormDescription>
                              Get a summary of your writing progress
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <Separator />
                    
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium">App Preferences</h4>
                      <FormField
                        control={preferencesForm.control}
                        name="darkMode"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base flex items-center gap-2">
                                <Moon className="h-4 w-4" />
                                Dark Mode
                              </FormLabel>
                              <FormDescription>
                                Switch between light and dark themes
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={(val) => {
                                  field.onChange(val);
                                  setDark(val);
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={preferencesForm.control}
                        name="autoSave"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Auto Save</FormLabel>
                              <FormDescription>
                                Automatically save your work as you type
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={preferencesForm.control}
                        name="showWordCount"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Show Word Count</FormLabel>
                              <FormDescription>
                                Display word count in the editor
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={updatePreferencesMutation.isPending}
                    >
                      {updatePreferencesMutation.isPending ? "Saving..." : "Save Preferences"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Change Password
                </CardTitle>
                <CardDescription>
                  Update your account password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...passwordForm}>
                  <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
                    <FormField
                      control={passwordForm.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Password</FormLabel>
                          <FormControl>
                            <Input {...field} type="password" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={passwordForm.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <Input {...field} type="password" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={passwordForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <Input {...field} type="password" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      disabled={updatePasswordMutation.isPending}
                    >
                      {updatePasswordMutation.isPending ? "Updating..." : "Update Password"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="llm-settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <SettingsIcon className="h-5 w-5" />
                  LLM Service & Model Selection
                </CardTitle>
                <CardDescription>
                  Choose your preferred LLM service and model for different tasks.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...llmSettingsForm}>
                  <form onSubmit={llmSettingsForm.handleSubmit(onSubmitLLMSettings)} className="space-y-6">
                    {/* Blueprint Generation */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold">Blueprint Generation</h3>
                      <FormField
                        control={llmSettingsForm.control}
                        name="blueprintService"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LLM Service</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an LLM service" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLLMServices.map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Choose the LLM service for generating story blueprints.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={llmSettingsForm.control}
                        name="blueprintModel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LLM Model</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an LLM model" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLLMModels[llmSettingsForm.watch("blueprintService") as keyof typeof availableLLMModels]?.map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name} (Multiplier: {model.multiplier}x)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Select the specific model. Higher multipliers mean more credit usage.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    {/* Chapters Outline */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold">Chapters Outline Generation</h3>
                      <FormField
                        control={llmSettingsForm.control}
                        name="chaptersOutlineService"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LLM Service</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an LLM service" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLLMServices.map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Choose the LLM service for generating chapter outlines.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={llmSettingsForm.control}
                        name="chaptersOutlineModel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LLM Model</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an LLM model" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLLMModels[llmSettingsForm.watch("chaptersOutlineService") as keyof typeof availableLLMModels]?.map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name} (Multiplier: {model.multiplier}x)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Select the specific model. Higher multipliers mean more credit usage.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    {/* Chapters Text */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold">Chapters Text Generation</h3>
                      <FormField
                        control={llmSettingsForm.control}
                        name="chaptersTextService"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LLM Service</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an LLM service" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLLMServices.map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Choose the LLM service for generating chapter text.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={llmSettingsForm.control}
                        name="chaptersTextModel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LLM Model</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an LLM model" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLLMModels[llmSettingsForm.watch("chaptersTextService") as keyof typeof availableLLMModels]?.map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name} (Multiplier: {model.multiplier}x)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Select the specific model. Higher multipliers mean more credit usage.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={updateLLMSettingsMutation.isPending}
                    >
                      {updateLLMSettingsMutation.isPending ? "Saving..." : "Save LLM Settings"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
